import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { inject as controller } from '@ember/controller';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { queryEncode, safeQueryDecode } from 'cio/helpers/query';
import { isEmptyFiltersQuery } from 'cio/utils/filters';
import { SortDirection } from 'cio/utils/sort';

import CollectionController from 'cio/pods/collections/collection/controller';
import { SerializedLegacyFilters } from 'cio/types/conditions/legacy-filter';
import { Filters } from 'cio/utils/filters';
import CollectionModel from 'cio/models/collection';
import AuthService from 'cio/services/auth';

const DEFAULT_VALUES = {
  filters: null,
  visibleColumns: [],
  viewAs: 'table' as 'table' | 'raw',
  page: 1,
  sort: null,
  asc: false,
  showColumnsModal: false,
};

export default class CollectionDataController extends Controller {
  @service auth!: AuthService;

  declare model: CollectionModel;

  @controller('collections/collection') collectionController!: CollectionController;

  queryParams = ['viewAs', 'filters', 'page', 'sort', 'asc'];
  viewAs: 'table' | 'raw' = DEFAULT_VALUES.viewAs;
  filters: string | null = DEFAULT_VALUES.filters;
  page = DEFAULT_VALUES.page;
  sort: string | null = DEFAULT_VALUES.sort;
  asc = DEFAULT_VALUES.asc;

  conditionTypes = ['collection_attribute'];
  perPage = 50;
  showColumnsModal = DEFAULT_VALUES.showColumnsModal;
  visibleColumns: string[] = DEFAULT_VALUES.visibleColumns;
  viewAsOptions = [
    { label: 'Table', value: 'table' },
    { label: 'Raw data', value: 'raw' },
  ];

  @alias('collectionController.model.schema') collectionSchema!: string[];

  @computed('filters.[]')
  get filterConditions() {
    return safeQueryDecode(this.filters ?? '');
  }

  setDefaults() {
    this.setProperties(DEFAULT_VALUES);
  }

  saveColumnsTask = task({ drop: true }, async (displayedAttributes: string[]) => {
    let collection = this.collectionController.model;

    try {
      collection.setProperties({
        displayedAttributes,
        updateType: 'displayed_attributes',
      });
      await collection.save();
      collection.resetUpdateType();
    } catch {
      collection.rollbackAttributes();
    }
  });

  setVisibleColumns() {
    let collection = this.collectionController.model;
    let { displayedAttributes } = collection;

    let visibleColumns = displayedAttributes?.length > 0 ? displayedAttributes : collection.schema.slice(0, 5);

    this.set('visibleColumns', visibleColumns);
  }

  @action
  updateColumns(columns: string[]) {
    this.setProperties({
      visibleColumns: columns,
      showColumnsModal: false,
    });
    this.saveColumnsTask.perform(columns);
  }

  @action
  updateFilters(conditions: SerializedLegacyFilters | Filters) {
    let filterString = queryEncode(conditions);

    // Reset the query param to nothing
    if (isEmptyFiltersQuery(filterString)) {
      this.set('filters', null);
    } else {
      this.set('filters', filterString);
    }
  }

  @action
  setSort(sort: string, sortDirection: SortDirection) {
    this.setProperties({
      sort,
      asc: sortDirection === SortDirection.Ascending,
    });
  }
}
